import { getAssetNames } from "actions/asset";
import { getDealChannels, saveChannel } from "actions/channel";
import {
  createDealStageAsset,
  deleteDealStageAsset,
  getDealStageAssets,
} from "actions/dealStageAsset";
import { showError, showInfo, showSuccess } from "actions/notification";
import { getDealPosts } from "actions/post";
import { getProposals } from "actions/proposal";
import { getDealSpaces } from "actions/space";
import { getTenant, saveTenant } from "actions/tenant";
import { getTenantContacts, saveTenantContact } from "actions/tenant_contact";
import {
  cancelDeal as cancel,
  createDeal as create,
  deleteDeal as destroyDeal,
  disregardDeal as disregard,
  duplicateDeal as duplicate,
  executeDeal as execute,
  exportPdf as exportAsPdf,
  exportXls as exportAsXls,
  getDeal as retrieveDeal,
  getDeals as retrieveDeals,
  renewDeal as renew,
  reopenDeal as reopen,
  saveDeal as persistDeal,
  subscribeDeal as subscribe,
  unsubscribeDeal as unsubscribe,
  updateDealPointPerson as updatePointPerson,
  updateDealShareStatus as updateShareStatus,
  updateDealStage as updateStage,
} from "api/deal";
import { push, replace } from "connected-react-router";
import { saveAs } from "file-saver";
import includes from "lodash/includes";
import isEmpty from "lodash/isEmpty";

import dealName from "utils/deals/name";
import dealRange from "utils/deals/range";
import removeKeys from "utils/object/removeKeys";
import Translate from "utils/translate";
import { createUrlParams } from "utils/urlParams";

const { t } = new Translate("notifications");

export const exportXls = () => (dispatch, getState) =>
  dispatch({
    type: "DEALS_XLS_EXPORT",
    payload: exportAsXls(getState().filteredDeals.map((d) => d.id)),
    meta: { dealIDs: getState().filteredDeals.map((d) => d.id) },
  }).then((res) =>
    res.value.blob().then((csv) => {
      const fullName = getState().currentUser.fullName.replace(/ /g, "").toLowerCase();

      const blob = new Blob([csv], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      saveAs(blob, `${fullName}_deals.xlsx`);
    })
  );

export const exportPdf = (filters) => (dispatch, getState) =>
  dispatch({
    type: "DEALS_PDF_EXPORT",
    payload: exportAsPdf(
      getState().filteredDeals.map((d) => d.id),
      filters
    ),
    meta: {
      dealIDs: getState().filteredDeals.map((d) => d.id),
      filters,
    },
  }).then(() => {
    dispatch(showInfo(t("deal.exportPdf.success.title"), t("deal.exportPdf.success.message")));
  });

export const filterDeals = (filters) => (dispatch) => {
  dispatch(replace(`/deals${createUrlParams(filters, "indices")}`));

  return dispatch({
    type: "DEALS_FILTER",
    meta: { filters },
  });
};

export const getDeals = (filters) => ({
  type: "DEALS_GET",
  payload: retrieveDeals(filters.portfolioIDs, filters.status),
});

export const sortDeals = (key, dir) => ({
  type: "DEALS_SORT",
  payload: { key, dir },
});

export const getDeal = (dealID) => (dispatch) => {
  dispatch(getTenant(dealID));
  dispatch(getTenantContacts(dealID));
  dispatch(getDealChannels(dealID));
  dispatch(getDealPosts(dealID));
  dispatch(getProposals(dealID));

  return dispatch({
    type: "DEAL_GET",
    payload: retrieveDeal(dealID),
  }).then(({ value }) => {
    Promise.all((value.deal.assets || []).map((asset) => dispatch(getDealSpaces(asset.id))));

    return dispatch(getAssetNames());
  });
};

export const saveDeal = (deal) => (dispatch) =>
  dispatch({
    type: "DEAL_SAVE",
    payload: persistDeal({
      ...deal,
      size: dealRange(deal.size),
      budget: dealRange(deal.budget),
    }),
  });

export const deleteDeal = (dealID) => (dispatch) =>
  dispatch({
    type: "DEAL_DELETE",
    meta: { dealID },
    payload: destroyDeal(dealID),
  })
    .then(() => dispatch(showSuccess(t("deal.delete.success.title"))))
    .catch((r) => {
      dispatch(showError(t("deal.delete.error.title"), t("deal.delete.error.message")));
      throw r;
    });

export const disregardDeal = (deal, disregardFeedback) => (dispatch) =>
  dispatch({
    type: "DEAL_DISREGARD",
    payload: disregard(deal, disregardFeedback),
  });

export const cancelDeal = (deal, cancelFeedback) => (dispatch) =>
  dispatch({
    type: "DEAL_CANCEL",
    payload: cancel(deal, cancelFeedback),
  });

export const reopenDeal = (dealID) => (dispatch) =>
  dispatch({
    type: "DEAL_REOPEN",
    payload: reopen(dealID),
  });

export const executeDeal = (deal, details) => (dispatch) =>
  dispatch({
    type: "DEAL_EXECUTE",
    payload: execute(deal, details),
  });

export const renewDeal = (dealID) => (dispatch) =>
  dispatch({
    type: "DEAL_RENEW",
    payload: renew(dealID),
  }).then((res) => {
    dispatch(showSuccess(t("deal.renew.success.title")));

    return res.value.deal;
  });

export const duplicateDeal = (dealID, isRenewal) => (dispatch) =>
  dispatch({
    type: "DEAL_DUPLICATE",
    payload: duplicate(dealID, isRenewal),
  }).then((res) => {
    dispatch(showSuccess(t("deal.duplicate.success.title")));

    return res.value.deal;
  });

export const updateAssets = (dealID, assetIDs) => (dispatch, getState) => {
  const { deal, dealStageAssets } = getState();
  const existingDealStageAssetIdsInCurrentStage = dealStageAssets
    .filter(
      (dealStageAsset) => dealStageAsset.dealId === deal.id && dealStageAsset.stage === deal.stage
    )
    .map((dealStageAsset) => dealStageAsset.asset.id);

  const dealStageAssetsToDelete = dealStageAssets.filter(
    (dealStageAsset) =>
      dealStageAsset.dealId === deal.id && !includes(assetIDs, dealStageAsset.asset.id)
  );

  const assetIDsToCreate = assetIDs.filter(
    (assetID) => !includes(existingDealStageAssetIdsInCurrentStage, assetID)
  );

  const deletePromises = dealStageAssetsToDelete.map((dealStageAsset) =>
    dispatch(deleteDealStageAsset(dealStageAsset))
  );

  const createPromises = assetIDsToCreate.map((assetId) =>
    dispatch(createDealStageAsset({ assetId, dealId: dealID, stage: deal.stage }))
  );

  return Promise.all([...deletePromises, ...createPromises]).then(() => {
    dispatch({
      type: "DEAL_UPDATE_ASSETS",
      meta: { assetIDs },
    });

    return Promise.all(assetIDs.map((id) => dispatch(getDealSpaces(id))));
  });
};

export const updateDealShareStatus = (dealID, shareStatus, sharedBrokerIDs) => (dispatch) =>
  dispatch({
    type: "DEAL_UPDATE_SHARE_STATUS",
    payload: updateShareStatus(dealID, shareStatus, sharedBrokerIDs),
  });

export const updateDealStage = (dealID, stage, assetIds) => (dispatch) =>
  dispatch({
    type: "DEAL_UPDATE_STAGE",
    payload: updateStage(dealID, { assetIds, stage }),
    meta: { dealID, stage, assetIds },
  }).then(() => dispatch(getDealStageAssets(dealID)));

export const updateDealPointPerson = (dealID, pointPersonID) => (dispatch) =>
  dispatch({
    type: "DEAL_UPDATE_POINT_PERSON",
    payload: updatePointPerson(dealID, pointPersonID),
  });

export const subscribeDeal = (dealID) => (dispatch) =>
  dispatch({
    type: "DEAL_SUBSCRIBE",
    payload: subscribe(dealID),
  });

export const unsubscribeDeal =
  (dealID, notify = false) =>
  (dispatch) =>
    dispatch({
      type: "DEAL_UNSUBSCRIBE",
      payload: unsubscribe(dealID),
    }).then(() => {
      if (notify) {
        dispatch(
          showSuccess(t("deal.unsubscribe.success.title"), t("deal.unsubscribe.success.message"))
        );
      }
    });

export const createDeal = (deal, portfolio, assets) => (dispatch) =>
  dispatch({
    type: "DEAL_CREATE",
    payload: create({
      sharedBrokerIDs: deal["_brokers"].map((broker) => broker.id), // eslint-disable-line
      ...removeKeys(deal, (key) => key.startsWith("_")),
      size: dealRange(deal.size),
      name: dealName(deal),
      assets: undefined,
      portfolio: undefined,
      portfolioID: portfolio.id,
      assetIDs: assets.map((asset) => asset.id),
    }),
    meta: { attributes: deal },
  }).then((res) => {
    const dealID = res.value.deal.id;
    const { channel, tenant, tenantContact } = deal;
    const promises = [];

    if (!isEmpty(deal.channel)) {
      promises.push(dispatch(saveChannel(dealID, { ...channel, id: undefined })));
    }
    if (!isEmpty(deal.tenant)) {
      promises.push(dispatch(saveTenant(dealID, { ...tenant, id: undefined })));
    }
    if (!isEmpty(deal.tenantContact)) {
      promises.push(dispatch(saveTenantContact(dealID, { ...tenantContact, id: undefined })));
    }

    return Promise.all(promises).then(() => dispatch(push(`/deals/${dealID}`)));
  });
